// Thin typed wrapper over `api` for every backend endpoint. Each call returns
// the JSON payload directly; ApiError surfaces server errors with a `code` we
// can branch on (e.g. "permission_denied", "failed_precondition").

import { api } from "./api";

// ── Shapes (loose: backend uses camelCase Firestore docs) ──────────────────

export type AccountType =
  | "platform_admin"
  | "individual"
  | "institution"
  | "enterprise_owner"
  | "enterprise_member";

export type Tier = {
  slug: string;
  name: string;
  // Flat monthly price for the whole org (Stripe quantity is always 1).
  priceEurPerMonth: number;
  // Cap on active members the org can invite. The pool below is shared.
  maxSeats: number;
  monthlyCreditsLimit?: number;
  storageBytesLimit?: number;
  requestsPerMinuteLimit?: number;
  features?: Record<string, unknown>;
  restrictedToOrgId?: string | null;
};

export type OrgTrial = {
  // Timestamps come back as Firestore-encoded objects ({_seconds, ...});
  // callers should funnel them through fmtTs / millisFromTs helpers.
  startedAt?: unknown;
  expiresAt?: unknown;
  claimedByUserUid?: string;
  claimedEmailHash?: string;
};

export type PendingPlanRequest = {
  tierSlug: "tier-2" | "enterprise";
  requestedAt?: unknown;
  requestedByUid?: string;
};

export type Org = {
  id: string;
  name: string;
  type: "enterprise" | "institution" | "free";
  status: "active" | "suspended";
  ownerUid?: string;
  ownerId?: string;
  activeTier: "tier-1" | "tier-2" | "trial" | null;
  seatCount: number;
  fiscal?: OrgFiscal | null;
  stripeCustomerId?: string | null;
  stripeSubscriptionId?: string | null;
  subscriptionStatus?: string | null;
  defaultPoolId?: string | null;
  designPartner?: boolean;
  onboardingCompleted?: boolean;
  onboardingCompletedAt?: unknown;
  logoUrl?: string | null;
  logoContentType?: string | null;
  logoUpdatedAt?: unknown;
  createdAt?: unknown;
  trial?: OrgTrial | null;
  pendingPlanRequest?: PendingPlanRequest | null;
};

export type OrgRole = "owner" | "admin" | "member" | "external";
export type OveragePolicy = "block" | "billed";

export type DbPool = {
  id?: string;
  orgId: string;
  scope: "org" | "workspace";
  scopeRefId: string;
  tierSlug: string | null;
  monthlyCreditsLimit: number;
  storageBytesLimit: number;
  rpmLimit: number;
  perUserCapPct: number;
  overagePolicy: OveragePolicy;
  overageEurPerMillionCredits: number;
  alertThresholds: number[];
  alertsFiredFor?: number[];
  allocationMode: "equal" | "manual";
  allocations: Record<string, { pct?: number; abs?: number }>;
  killSwitch: boolean;
  storageFull: boolean;
  // Credit-window counter: resets at each payment/renewal. The only figure
  // that may be divided by `monthlyCreditsLimit`.
  creditsUsedThisMonth: number;
  storageBytesUsed: number;
};

export type WorkspaceRolePolicy = {
  access: { owner: boolean; admin: boolean; member: boolean };
  administer: { owner: boolean; admin: boolean; member: boolean };
};

export type WorkspaceUidOverride = "access" | "admin" | "denied";

export type DbWorkspace = {
  id: string;
  orgId: string;
  name: string;
  createdByUid: string;
  storageBytesUsed: number;
  rolePolicy: WorkspaceRolePolicy;
  uidOverrides: Record<string, WorkspaceUidOverride>;
  poolId: string | null;
};

export type TaxIdType =
  | "es_cif"
  | "es_dni"
  | "eu_vat"
  | "gb_vat"
  | "ch_vat"
  | "us_ein"
  | "no_vat";

export type OrgFiscal = {
  nif: string;
  taxIdType?: TaxIdType;
  razonSocial: string;
  direccionFiscal: {
    line1: string;
    line2: string | null;
    city: string;
    postalCode: string;
    region: string | null;
  };
  pais: string;
  emailFacturacion: string;
  viesValidated?: boolean;
};

export type Member = {
  uid: string;
  email?: string | null;
  role: OrgRole;
  addedAt?: unknown;
  usage?: {
    // Credit window, not calendar month — comparable with the member cap.
    creditsUsedThisWindow?: number;
  };
};

export type Invite = {
  id: string;
  email: string;
  kind?: "org_owner" | "member";
  orgId?: string;
  orgName?: string;
  designPartner?: boolean;
  status: "pending" | "accepted";
  invitedAt?: unknown;
};

export type ApiKey = {
  keyHash: string;
  orgId: string;
  ownerUid: string;
  name: string;
  keyPrefix: string;
  keyLast4: string;
  status: "active" | "revoked";
  createdAt?: unknown;
};

export type Proposal = {
  id: string;
  orgId: string;
  concept: string;
  amountEur: number;
  status: "sent" | "paid" | "expired" | "canceled";
  paymentLink?: string;
  invoicePdf?: string | null;
  invoiceHostedUrl?: string | null;
  createdAt?: unknown;
  paidAt?: unknown;
};

export type Subscription = {
  id: string;
  orgId: string;
  orgName?: string | null;
  tierSlug?: string;
  quantity: number;
  status: string;
  cancelAtPeriodEnd?: boolean;
  currentPeriodEnd?: unknown;
  stripeCustomerId?: string;
};

// ── Auth ─────────────────────────────────────────────────────────────────

export const Auth = {
  // displayName is optional now: when omitted/empty the backend falls back
  // to the email-prefix and the in-app onboarding (StepName) overrides it
  // via Profile.updateDisplayName.
  initialize: (token: string, displayName?: string) =>
    api.post<{ status: string; orgId?: string; role?: string }>(
      "/auth/initialize",
      { token, displayName: displayName ?? "" },
    ),
  invitePreview: (token: string) =>
    api.get<{
      email: string;
      kind: "org_owner" | "member";
      orgName: string | null;
      expiresAt: string | null;
    }>(`/auth/invite/${encodeURIComponent(token)}`),
  me: () =>
    api.get<{
      auth: {
        uid: string;
        email: string | null;
        isPlatformAdmin: boolean;
        accountType: AccountType | null;
        enterpriseId: string | null;
        institutionId: string | null;
      };
      profile: Record<string, unknown> | null;
    }>("/auth/me"),
  myMemberships: () =>
    api.get<{
      memberships: Array<{
        orgId: string;
        orgName: string;
        orgType: "enterprise" | "institution" | "free";
        role: "owner" | "admin" | "member" | "external";
      }>;
    }>("/auth/me/memberships"),
  exportData: () => api.get<Record<string, unknown>>("/auth/me/data-export"),
  deleteAccount: () => api.post<{ status: string }>("/auth/me/delete", {}),
};

// ── Admin invites (org owners) ──────────────────────────────────────────

export const Invites = {
  list: () => api.get<{ invites: Invite[] }>("/admin/invites"),
  create: (input: { email: string; orgName: string; designPartner?: boolean }) =>
    api.post<{ status: string; email: string }>("/admin/invites", input),
  revoke: (email: string) =>
    api.delete<{ status: string }>(
      `/admin/invites/${encodeURIComponent(email)}`,
    ),
};

export const Profile = {
  updateDisplayName: (displayName: string) =>
    api.patch<{ ok: true }>("/auth/me/profile", { displayName }),
};

export const Onboarding = {
  complete: (orgId: string) =>
    api.post<{ ok: true; alreadyCompleted?: boolean }>(
      `/orgs/${orgId}/onboarding/complete`,
      {},
    ),
  completeForUser: () =>
    api.post<{ ok: true; alreadyCompleted?: boolean }>(
      `/auth/me/onboarding/complete`,
      {},
    ),
};

export const Plans = {
  activateTrial: (orgId: string) =>
    api.post<{ status: string; tierSlug: string; expiresAt: number }>(
      `/orgs/${orgId}/trial/activate`,
      {},
    ),
  requestPlan: (orgId: string, tierSlug: "tier-2" | "enterprise") =>
    api.post<{ status: string; tierSlug: string }>(
      `/orgs/${orgId}/plans/request`,
      { tierSlug },
    ),
};

export const Tour = {
  complete: (input?: { skipped?: boolean }) =>
    api.post<{ ok: true; alreadyCompleted?: boolean }>(
      `/auth/me/tour-complete`,
      { skipped: input?.skipped === true },
    ),
};

// ── Org / fiscal / Stripe customer ──────────────────────────────────────

export const Orgs = {
  get: (orgId: string) => api.get<{ org: Org }>(`/orgs/${orgId}`),
  updateFiscal: (
    orgId: string,
    fiscal: OrgFiscal,
    extra?: {
      consent?: { version: string; accepted: true };
      taxIdType?: string;
    },
  ) =>
    api.patch<{
      ok: boolean;
      viesValidated: boolean;
      stripeCustomerCreated: boolean;
    }>(`/orgs/${orgId}/fiscal`, { ...fiscal, ...extra }),
  invoices: (orgId: string) =>
    api.get<{
      invoices: Array<{
        id: string;
        number: string | null;
        kind: "subscription" | "oneoff";
        status: "open" | "paid" | "uncollectible" | string;
        total: number;
        currency: string;
        description: string;
        created: string | null;
        paidAt: string | null;
        hostedUrl: string | null;
        pdfUrl: string | null;
      }>;
    }>(`/orgs/${orgId}/invoices`),
  ensureStripeCustomer: (orgId: string) =>
    api.post<{ customerId: string; created: boolean }>(
      `/orgs/${orgId}/stripe-customer`,
    ),
  uploadLogo: (orgId: string, file: Blob) =>
    api.postBinary<{ ok: true; logoUrl: string }>(
      `/orgs/${orgId}/branding/logo`,
      file,
    ),
  deleteLogo: (orgId: string) =>
    api.delete<{ ok: true; alreadyEmpty?: boolean }>(
      `/orgs/${orgId}/branding/logo`,
    ),
};

// ── Proposals ────────────────────────────────────────────────────────────

export const Proposals = {
  list: () => api.get<{ proposals: Proposal[] }>("/proposals"),
  create: (input: {
    orgId: string;
    concept: string;
    amountEur: number;
    expiresInDays?: number;
  }) =>
    api.post<{ proposalId: string; paymentLink?: string }>("/proposals", input),
  update: (
    proposalId: string,
    input: {
      concept: string;
      amountEur: number;
      expiresInDays?: number;
    },
  ) =>
    api.patch<{ proposalId: string; paymentLink?: string }>(
      `/proposals/${proposalId}`,
      input,
    ),
  cancel: (proposalId: string) =>
    api.delete<{ ok: boolean }>(`/proposals/${proposalId}`),
};

// ── Subscriptions ────────────────────────────────────────────────────────

export type CatalogTier = {
  id: string;
  slug: string;
  name: string;
  priceEurPerMonth: number;
  maxSeats: number;
  monthlyCreditsLimit: number;
  storageBytesLimit: number;
  requestsPerMinuteLimit: number;
  features: Record<string, unknown>;
  // When set, the tier is a custom plan reserved for one specific org.
  restrictedToOrgId?: string | null;
};

export type TierPatch = {
  name?: string;
  priceEurPerMonth?: number;
  maxSeats?: number;
  monthlyCreditsLimit?: number;
  storageBytesLimit?: number;
  requestsPerMinuteLimit?: number;
  features?: { artifacts?: boolean; prioritySupport?: boolean };
};

export type TierCreate = {
  slug: string;
  name: string;
  priceEurPerMonth: number;
  maxSeats: number;
  monthlyCreditsLimit: number;
  storageBytesLimit: number;
  requestsPerMinuteLimit: number;
  features: { artifacts?: boolean; prioritySupport?: boolean };
  // Optional: scope this tier to a single org (custom deal).
  restrictedToOrgId?: string;
};

// ── Admin: model pricing + global pricing (credit conversion) ───────────

export type TokenType =
  | "input"
  | "output"
  | "cachedRead"
  | "cacheCreation"
  | "thinking";

export type ModelPricing = {
  id: string;
  modelId: string;
  provider: "anthropic" | "openai" | "google" | "openrouter";
  displayName: string;
  prices: Record<TokenType, number>;
  // Optional manual override for the "×" column. If null/undefined, the
  // value is auto-computed from price ratio vs the baseline model.
  displayMultiplier?: number | null;
  isActive: boolean;
  deprecated?: boolean;
};

export type GlobalPricingConfig = {
  usdPerCredit: number | null;
  baselineModelId: string | null;
  baselineTokenType: TokenType | null;
};

export const AdminModelPricing = {
  list: () =>
    api.get<{ models: ModelPricing[] }>("/admin/model-pricing"),
  create: (body: {
    modelId: string;
    provider: ModelPricing["provider"];
    displayName: string;
    prices: Record<TokenType, number>;
    displayMultiplier?: number | null;
    isActive?: boolean;
  }) => api.post<{ modelId: string }>("/admin/model-pricing", body),
  update: (
    modelId: string,
    // Provider intentionally NOT here — fixed at create time.
    patch: Partial<{
      displayName: string;
      prices: Partial<Record<TokenType, number>>;
      displayMultiplier: number | null;
      isActive: boolean;
      deprecated: boolean;
    }>,
  ) =>
    api.patch<{ modelId: string }>(
      `/admin/model-pricing/${encodeURIComponent(modelId)}`,
      patch,
    ),
  // Two-step: deprecates an active model; permanently deletes an already
  // deprecated one (the server refuses to delete the pricing baseline).
  remove: (modelId: string) =>
    api.delete<{ modelId: string; deleted?: boolean; deprecated?: boolean }>(
      `/admin/model-pricing/${encodeURIComponent(modelId)}`,
    ),
  seed: (opts?: { force?: boolean }) =>
    api.post<{
      created: string[];
      updated?: string[];
      skipped: string[];
      globalCreated: boolean;
    }>(
      `/admin/model-pricing/seed${opts?.force ? "?force=true" : ""}`,
      {},
    ),
};

export const AdminGlobalPricing = {
  get: () => api.get<{ config: GlobalPricingConfig }>("/admin/global-pricing"),
  update: (patch: Partial<GlobalPricingConfig>) =>
    api.patch<{ ok: boolean }>("/admin/global-pricing", patch),
};

export const AdminTiers = {
  list: () =>
    api.get<{
      tiers: (CatalogTier & { stripePriceId: string })[];
    }>("/admin/tiers"),
  create: (body: TierCreate) =>
    api.post<{
      slug: string;
      stripePriceId: string;
      stripeProductId: string;
    }>("/admin/tiers", body),
  update: (slug: string, patch: TierPatch) =>
    api.patch<{
      slug: string;
      affectedSubscriptions: number;
      newStripePriceId: string | null;
    }>(`/admin/tiers/${slug}`, patch),
  delete: (slug: string) =>
    api.delete<{ status: string; slug: string }>(`/admin/tiers/${slug}`),
};

export type CatalogResponse = {
  tiers: CatalogTier[];
  trial: {
    slug: string;
    durationDays: number;
    // false if /trialClaims/{hashEmail(caller)} already exists.
    available: boolean;
  };
};

export const Subscriptions = {
  catalog: () => api.get<CatalogResponse>("/tiers"),
  acceptTerms: (orgId: string, version = "v1") =>
    api.post<{ status: string; version: string }>(
      `/orgs/${orgId}/subscription/accept-terms`,
      { version },
    ),
  get: (orgId: string) =>
    api.get<{
      orgId: string;
      activeTier: string | null;
      seatCount: number;
      subscriptionStatus: string | null;
      stripeSubscriptionId: string | null;
      tier: Tier | null;
      subscription: Subscription | null;
    }>(`/orgs/${orgId}/subscription`),
  checkout: (
    orgId: string,
    body?: { successUrl?: string; cancelUrl?: string },
  ) =>
    api.post<{ checkoutUrl: string; checkoutSessionId: string }>(
      `/orgs/${orgId}/subscription/checkout`,
      body ?? {},
    ),
  portal: (orgId: string, body?: { returnUrl?: string }) =>
    api.post<{ portalUrl: string }>(
      `/orgs/${orgId}/subscription/portal`,
      body ?? {},
    ),
  cancel: (orgId: string, immediate = false) =>
    api.delete<{ status: string; cancelAtPeriodEnd: boolean }>(
      `/orgs/${orgId}/subscription${immediate ? "?immediate=true" : ""}`,
    ),
  listAll: () =>
    api.get<{ subscriptions: Subscription[] }>("/admin/subscriptions"),
};

// ── Team (members) ───────────────────────────────────────────────────────

export const Team = {
  list: (orgId: string) =>
    api.get<{
      orgId: string;
      seatCount: number;
      seatsInUse: number;
      activeTier: string | null;
      tier: Tier | null;
      pool: DbPool | null;
      members: Member[];
      invites: Invite[];
    }>(`/orgs/${orgId}/members`),
  invite: (
    orgId: string,
    email: string,
    role: "admin" | "member" | "external" = "member",
  ) =>
    api.post<{ status: string; role: string }>(`/orgs/${orgId}/members`, {
      email,
      role,
    }),
  cancelInvite: (orgId: string, email: string) =>
    api.delete<{ status: string }>(
      `/orgs/${orgId}/invites/${encodeURIComponent(email)}`,
    ),
  remove: (orgId: string, uid: string) =>
    api.delete<{ status: string }>(`/orgs/${orgId}/members/${uid}`),
  setRole: (orgId: string, uid: string, role: "admin" | "member" | "external") =>
    api.patch<{ status: string; role: string }>(
      `/orgs/${orgId}/members/${uid}/role`,
      { role },
    ),
};

// ── Pool (org credit + storage shared budget) ───────────────────────────

export const Pools = {
  get: (orgId: string) =>
    api.get<{ pool: DbPool }>(`/orgs/${orgId}/pool`),
  usage: (orgId: string, period?: string) =>
    api.get<{
      poolId: string;
      period: string;
      pool: DbPool;
      aggregate: {
        totalCreditsUsed?: number;
        totalRequests?: number;
        storageBytesUsed?: number;
        byMember?: Record<
          string,
          { creditsUsed?: number; requestsCount?: number }
        >;
      } | null;
    }>(
      `/orgs/${orgId}/pool/usage${period ? `?period=${encodeURIComponent(period)}` : ""}`,
    ),
  me: (orgId: string) =>
    api.get<{
      poolId: string;
      role: OrgRole;
      // Calendar month (`YYYY-MM`) covering `memberUsage` only.
      period: string;
      monthlyCreditsLimit: number;
      // --- credit-window clock: comparable with monthlyCreditsLimit ---
      creditsUsedThisMonth: number;
      creditsUsedThisWindow: number;
      creditWindowStartMs: number | null;
      storageBytesLimit: number;
      storageBytesUsed: number;
      rpmLimit: number;
      overagePolicy: OveragePolicy;
      overageEurPerMillionCredits: number;
      killSwitch: boolean;
      storageFull: boolean;
      // --- calendar-month mirror: breakdown + analytics, never a ratio
      // against monthlyCreditsLimit ---
      memberUsage: {
        creditsUsed?: number;
        requestsCount?: number;
        byModel?: Record<
          string,
          {
            provider?: string;
            model?: string;
            displayName?: string;
            credits?: number;
            requests?: number;
          }
        >;
      };
    }>(`/orgs/${orgId}/pool/me`),
  updatePolicy: (
    orgId: string,
    patch: {
      perUserCapPct?: number;
      overagePolicy?: OveragePolicy;
      overageEurPerMillionCredits?: number;
      alertThresholds?: number[];
    },
  ) =>
    api.patch<{ status: string }>(`/orgs/${orgId}/pool`, patch),
  updateAllocations: (
    orgId: string,
    allocationMode: "equal" | "manual",
    allocations?: Record<string, { pct?: number; abs?: number }>,
  ) =>
    api.patch<{ status: string }>(`/orgs/${orgId}/pool/allocations`, {
      allocationMode,
      allocations,
    }),
  resetKillSwitch: (orgId: string) =>
    api.post<{ status: string }>(
      `/orgs/${orgId}/pool/reset-killswitch`,
      {},
    ),
};

// ── Workspaces ──────────────────────────────────────────────────────────

export type WorkspaceCreateBody = {
  name: string;
  rolePolicy?: WorkspaceRolePolicy;
  uidOverrides?: Record<string, WorkspaceUidOverride>;
};

export type WorkspacePatchBody = {
  name?: string;
  rolePolicy?: WorkspaceRolePolicy;
  uidOverrides?: Record<string, WorkspaceUidOverride>;
};

export const Workspaces = {
  list: (orgId: string) =>
    api.get<{ workspaces: DbWorkspace[] }>(`/orgs/${orgId}/workspaces`),
  create: (orgId: string, body: WorkspaceCreateBody) =>
    api.post<DbWorkspace>(`/orgs/${orgId}/workspaces`, body),
  update: (orgId: string, wsId: string, patch: WorkspacePatchBody) =>
    api.patch<{ status: string }>(
      `/orgs/${orgId}/workspaces/${wsId}`,
      patch,
    ),
  delete: (orgId: string, wsId: string) =>
    api.delete<{ status: string }>(`/orgs/${orgId}/workspaces/${wsId}`),
};

// ── Admin: orgs ──────────────────────────────────────────────────────────

export const AdminOrgs = {
  list: (params?: { type?: "enterprise" | "free" | "all"; pageSize?: number }) => {
    const q = new URLSearchParams();
    if (params?.type) q.set("type", params.type);
    if (params?.pageSize) q.set("pageSize", String(params.pageSize));
    return api.get<{ orgs: Org[]; total: number }>(
      `/admin/orgs${q.toString() ? `?${q}` : ""}`,
    );
  },
  details: (orgId: string) =>
    api.get<{
      org: Org;
      members: Member[];
      keys: ApiKey[];
      seatsInUse: number;
    }>(`/admin/orgs/${orgId}`),
  suspend: (orgId: string, action: "suspend" | "reactivate") =>
    api.patch<{ status: string }>(`/admin/orgs/${orgId}/suspend`, { action }),
  delete: (orgId: string) =>
    api.delete<{ status: string }>(`/admin/orgs/${orgId}`),
  setPlan: (
    orgId: string,
    activeTier: "tier-1" | "tier-2" | null,
    seatCount: number,
  ) =>
    api.patch<{
      orgId: string;
      activeTier: string | null;
      seatCount: number;
      seatsInUse: number;
    }>(`/admin/orgs/${orgId}/plan`, { activeTier, seatCount }),
  promote: (targetUid: string, orgName: string) =>
    api.post<{ status: string; orgId: string }>(`/admin/orgs/promote`, {
      targetUid,
      orgName,
    }),
  transfer: (orgId: string, newOwnerUid: string) =>
    api.post<{ status: string; orgId: string; newOwnerUid: string }>(
      `/admin/orgs/${orgId}/transfer`,
      { newOwnerUid },
    ),
  cancelSubscription: (orgId: string) =>
    api.post<{ status: string; stripeCancelled: boolean }>(
      `/admin/orgs/${orgId}/subscription/cancel`,
      {},
    ),
};

// ── Admin: users / finance / audit ───────────────────────────────────────

export const AdminUsers = {
  list: (params?: { pageSize?: number; startAfterId?: string }) => {
    const q = new URLSearchParams();
    if (params?.pageSize) q.set("pageSize", String(params.pageSize));
    if (params?.startAfterId) q.set("startAfterId", params.startAfterId);
    return api.get<{ users: Array<Record<string, unknown> & { uid: string }> }>(
      `/admin/users${q.toString() ? `?${q}` : ""}`,
    );
  },
  delete: (uid: string) =>
    api.delete<{ status: string }>(`/admin/users/${uid}`),
};

export const AdminFinance = {
  metrics: () =>
    api.get<{
      mrr: { eur: number; liveSubscriptions: number; seatsByTier: Record<string, number> };
      cancellations30d: number;
      pastDueCount: number;
      oneOffRevenue30dEur: number;
      tokensConsumed30d: number;
      generatedAt: string;
    }>("/admin/finance"),
  audit: (params?: { pageSize?: number; resourceId?: string }) => {
    const q = new URLSearchParams();
    if (params?.pageSize) q.set("pageSize", String(params.pageSize));
    if (params?.resourceId) q.set("resourceId", params.resourceId);
    return api.get<{
      logs: Array<{
        id: string;
        actorUid: string;
        action: string;
        resourceId: string;
        timestamp?: unknown;
      }>;
    }>(`/admin/finance/audit${q.toString() ? `?${q}` : ""}`);
  },
};

// ---- Agent observability ("Agentes" admin panel) ----

export type ObsTokens = {
  input: number;
  output: number;
  cached: number;
  cacheCreation: number;
  reasoning: number;
  total: number;
};

export type ObsRangeMeta = { from: string; to: string; days: number };

export type ObsOverview = {
  range: ObsRangeMeta;
  totals: {
    requests: number;
    tokens: ObsTokens;
    credits: number;
    usdCost: number;
    cacheHitRatio: number;
    activeOrgs: number;
  };
  // activeUsers: distinct-user count from raw events; available only while
  // the range sits inside the 90-day retention window. errorRate/latency:
  // null + available:false until the chat app instruments schemaVersion-3
  // telemetry. The UI shows unavailable metrics as "pending".
  activeUsers: { value: number | null; available: boolean };
  errorRate: { value: number | null; available: boolean };
  latency: { avgMs: number | null; p95Ms: number | null; available: boolean };
  generatedAt: string;
};

export type ObsModelRow = {
  modelId: string;
  displayName: string;
  provider: string;
  requests: number;
  tokens: ObsTokens;
  credits: number;
  usdCost: number;
  errorCount: number;
  cacheHitRatio: number;
  pct: number;
};

export type ObsTimePoint = {
  date: string;
  requests: number;
  costUsd: number;
  credits: number;
  tokens: ObsTokens;
  cacheHitRatio: number;
  errorRate: number | null;
  avgLatencyMs: number | null;
};

export type ObsTopRow = {
  id: string;
  label: string;
  requests: number;
  tokens: number;
  costUsd: number;
};

export type ObsToolRow = {
  name: string;
  calls: number;
  errors: number;
  errorRate: number;
};

// Intraday requests-per-minute over a short window (24h/48h). `values[i]` is the
// request count for the minute at `start + i*stepSec`; `peakRpm` is the true
// per-minute peak used to size the provider's RPM quota.
export type ObsRpm = {
  windowHours: number;
  start: string;
  stepSec: number;
  values: number[];
  peakRpm: number;
  peakAt: string | null;
  avgRpm: number;
  generatedAt: string;
};

type ObsRange = { from: string; to: string };

function obsQuery(params: Record<string, string | number | undefined>): string {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== "") q.set(k, String(v));
  }
  return q.toString();
}

export const AdminObservability = {
  overview: ({ from, to }: ObsRange) =>
    api.get<ObsOverview>(
      `/admin/agent-metrics/overview?${obsQuery({ from, to })}`,
    ),
  byModel: ({ from, to }: ObsRange) =>
    api.get<{ range: ObsRangeMeta; models: ObsModelRow[]; generatedAt: string }>(
      `/admin/agent-metrics/by-model?${obsQuery({ from, to })}`,
    ),
  timeseries: ({ from, to }: ObsRange) =>
    api.get<{ range: ObsRangeMeta; points: ObsTimePoint[] }>(
      `/admin/agent-metrics/timeseries?${obsQuery({ from, to })}`,
    ),
  topOrgs: ({ from, to, limit }: ObsRange & { limit?: number }) =>
    api.get<{ range: ObsRangeMeta; rows: ObsTopRow[]; source: string }>(
      `/admin/agent-metrics/top-orgs?${obsQuery({ from, to, limit })}`,
    ),
  tools: ({ from, to }: ObsRange) =>
    api.get<{ range: ObsRangeMeta; rows: ObsToolRow[] }>(
      `/admin/agent-metrics/tools?${obsQuery({ from, to })}`,
    ),
  rpm: ({ hours }: { hours: number }) =>
    api.get<ObsRpm>(`/admin/agent-metrics/rpm?${obsQuery({ hours })}`),
  exportBlob: ({
    from,
    to,
    format,
    dataset,
  }: ObsRange & { format: "csv" | "json"; dataset: "daily" | "raw" }) =>
    api.getBlob(
      `/admin/agent-metrics/export?${obsQuery({ from, to, format, dataset })}`,
    ),
};

// ---- Org-brain curation queue (owner-facing "Brain") ----
//
// Each owner/admin curates their OWN org's brain. account-app is a thin client:
// these hit its /brain proxy, which resolves the caller's org server-side and
// forwards to the `app` backend. The client never sends an orgId. `env` is an
// OPTIONAL override that the server honours ONLY for platform admins (testing
// against dev); for everyone else it's ignored and forced to prod. Shapes
// mirror the /internal/brain-queue contract.

export type BrainEnv = "dev" | "prod";

export type BrainProposalMeta = {
  action: "add" | "update" | "delete";
  slug: string;
  type: "terminology" | "criterio" | "estructura";
  title?: string;
  proposedAt: string;
  // YAML frontmatter → every meta value is a string, including this count.
  captures: string;
  rationale?: string;
};

export type BrainProposal = {
  file: string;
  meta: BrainProposalMeta;
  body: string;
};

export type BrainSummary = {
  bundles: Array<{ bundle: string; count: number }>;
};

export type BrainProposalList = {
  bundle: string;
  proposals: BrainProposal[];
};

// `env` is only ever passed by the platform-admin toggle; the server ignores
// it for non-admins. Omitted → server defaults to prod.
export const OwnerBrain = {
  summary: (env?: BrainEnv) => {
    const qs = env ? `?${new URLSearchParams({ env }).toString()}` : "";
    return api.get<BrainSummary>(`/brain/summary${qs}`);
  },
  list: (bundle: string, env?: BrainEnv) => {
    const params: Record<string, string> = { bundle };
    if (env) params.env = env;
    return api.get<BrainProposalList>(
      `/brain?${new URLSearchParams(params).toString()}`,
    );
  },
  accept: (bundle: string, file: string, env?: BrainEnv) =>
    api.post<{ applied: { action: string; slug: string } }>(`/brain/accept`, {
      bundle,
      file,
      ...(env ? { env } : {}),
    }),
  reject: (bundle: string, file: string, env?: BrainEnv) =>
    api.post<Record<string, never>>(`/brain/reject`, {
      bundle,
      file,
      ...(env ? { env } : {}),
    }),
};
